const saleCalculator = require('../../../../sales/saleCalculator');
const dateUtil = require('../../../../../dateUtil/dateUtil');
const constants = require('../../../../constants');
const logger = require('../../../../../logger/logger');
const localizationUtils = require('../../../../localizationUtils');
const pakistanEndorsementUtil = require('../pakistanEndorsementUtil');


//#region Documentation

//
// FBR = Federal Board Of Revenue, Pakistan
// endorsement handler for Pakistan FBR saleEndorsement integration
// applySaleEndorsement = add endorsement to sale.endorsements
// applyReceiptEndorsement = add endorsement related print lines to receipt
//

//
// FBR POS Integration knowledge base
// https://help.fbr.gov.pk/?p=6148
//

// https://customs.gov.np/storage/files/1/Custom%20Tariff/Customs%20Tariff%20%202077%20(2020-21)%20Eng.pdf
// Section IV: PREPARED FOODSTUFFS; BEVERAGES, SPIRITS AND VINEGAR; TOBACCO AND MANUFACTURED TOBACCO SUBSTITUTES
// PCTCode = Pakistan Customs Tariff Code
// Not more than 8 digits, "16010000" = meat based prepared foodstuff

// FBR Test Environment:
// FBR test environment requires test POSID/Access Code which can be generated from FBR POS integration portal and
// Jinoby PK is already registered there and can generate test codes any time

//#endregion


export default {
    applySaleEndorsement: applySaleEndorsement,
    getSaleEndorsement: getSaleEndorsement
}

async function applySaleEndorsement(sale, integration)
{
    if (!pakistanEndorsementUtil.isSaleEndorsementApplicable(sale, integration))
        return;

    if (integration.jsonData.isDeferred && canDefer(sale)) 
    {
        sale.endorsements.push({
            integrationId: integration.id,
            integrationType: integration.integrationType,
            externalEntity: integration.externalEntity,
            isDeferred: true
        });
    }
    else
    {
        const endorsement = await getSaleEndorsement(sale, integration);

        if (endorsement != null)
        {
            sale.endorsements.push(endorsement);
        }
    }
}

function canDefer (sale)
{
    return sale.paymentMethod === constants.PaymentMethodCash;
}

async function getSaleEndorsement(sale, integration) 
{
    // Returns FBR sale endorsement

    const serviceUrl = integration.jsonData.serviceUrl;
    const fbrInvoice = getFBRCompliantSaleInvoice(integration, sale);

    logger.info(`FBR: getSaleEndorsement request - propertyId=${sale.propertyId}; SaleId/Number=${sale.id || 'new sale'}/'${sale.number}'; FBR Invoice = ${JSON.stringify(fbrInvoice)}`);

    const request = {
        method: "POST",
        headers: getHttpHeaders(),
        body: JSON.stringify(fbrInvoice)
    };

    const response = await fetch(serviceUrl, request);

    if (response.status < 200 || response.status > 299)
    {
        let text = await response.text();
        text = text || `Failed to get FBR endorsement with response status = ${response.status}`;
        logger.error(`FBR: getSaleEndorsement http status error: ${text} `);
        throw new Error(text);
    }

    const responseData = await response.json();

    const endorsement = {
        integrationId: integration.id,
        integrationType: integration.integrationType,
        externalEntity: integration.externalEntity,
        responseData: responseData,
        isError: responseData.Code != "100",
    }

    if (endorsement.isError) 
    {
        logger.error(`FBR: getSaleEndorsement response - ${JSON.stringify(responseData)}`);
    }
    else
    {
        logger.info(`FBR: getSaleEndorsement response - ${JSON.stringify(responseData)}`);
    }

    return endorsement;

    //#region Sample responseData from FBR IMS Service

    // Note following are sample data from test environment and not from prod environment

    /*

    Sample successful responseData:

        {
            "InvoiceNumber": "800076BGBB14264834*test*",
            "Code": "100",
            "Response": "Fiscal Invoice Number generated successfully.",
            "Errors": null
        }

    Sample error responseData:
    
        {
            "InvoiceNumber": "Not Available",
            "Code": "402",
            "Response": "Model validation failed.",
            "Errors": "Required property 'TaxRate' not found "
        }

    */

    //#endregion
}

function getFBRCompliantSaleInvoice(integration, sale)
{
    let totalQuantity = 0;
    let totalSaleValue = 0;         // total sale amount exclusive of tax
    let totalTaxCharged = 0;        // total tax amount (FBR General Sales Tax)

    const invoiceItems = [];

    const isVatInclusive = localizationUtils.isVatInclusive();

    (function processSaleItems(saleItems)
    {
        for (const saleItem of saleItems)
        {
            // Ignore saleItems where unitPrice = 0

            if (saleItem.unitPrice > constants.numericThresholdValue) 
            {
                let saleValue = 0, taxCharged = 0, totalAmount = 0, taxRate = 0;

                taxCharged = saleCalculator.roundNumericValue(saleItem.quantity * saleItem.vat);

                if (isVatInclusive) 
                {
                    totalAmount = saleCalculator.roundNumericValue(saleItem.quantity * saleItem.unitPrice);
                    saleValue = saleCalculator.roundNumericValue(totalAmount - taxCharged);
                }
                else
                {
                    saleValue = saleCalculator.roundNumericValue(saleItem.quantity * saleItem.unitPrice);
                    totalAmount = saleValue + taxCharged;
                }

                taxRate = saleValue > 0
                    ? saleCalculator.roundNumericValue((taxCharged / saleValue) * 100)
                    : 0;

                // ItemCode: What is ItemCode, need to be checked with FBR
                // PCTCode: Pak Custom Tariff Code, should be configured in the menu for each item
                // TaxRate: GST rate in percentage
                // InvoiceType: What is InvoiceType, need to be checked with FBR, using fbrInvoiceTypes.new for now

                const invoiceItem = {
                    ItemCode: saleItem.caption,         // What is item code, need to be checked with FBR
                    ItemName: saleItem.caption,
                    PCTCode: "16",                      // PCTCode "16" = Meat based prepared foodstuff, hard-coded for now
                    Quantity: saleItem.quantity,
                    TaxRate: taxRate,
                    SaleValue: saleValue,
                    TaxCharged: taxCharged,
                    TotalAmount: totalAmount,
                    InvoiceType: getFBRInvoiceType(sale)
                };

                invoiceItems.push(invoiceItem);

                totalQuantity += invoiceItem.Quantity;
                totalSaleValue += invoiceItem.SaleValue;
                totalTaxCharged += invoiceItem.TaxCharged;
            }

            if (saleItem.saleItems.length > 0)
            {
                processSaleItems(saleItem.saleItems);   // Recursion
            }
        }
    })(sale.saleItems);     // IIF

    const totalCharges = saleCalculator.calculateTotalCharges(sale);     // No separate field for charges in FBR invoice
    totalTaxCharged += saleCalculator.calculateTotalChargesVAT(sale);    // vat on charges

    const totalRefunds = saleCalculator.calculateTotalRefunds(sale);     // No field for refund in FBR invoice
    const totalDiscount = saleCalculator.calculateTotalDiscounts(sale);

    const invoice = {
        InvoiceNumber: "",
        POSID: integration.jsonData.POSID,
        USIN: sale.number,                                          // User Invoice Number
        DateTime: dateUtil.convertToLocalStandard(sale.dateTime),
        TotalSaleValue: saleCalculator.roundNumericValue(totalSaleValue),
        TotalTaxCharged: saleCalculator.roundNumericValue(totalTaxCharged),
        TotalQuantity: totalQuantity,
        Discount: saleCalculator.roundNumericValue(totalDiscount),
        TotalBillAmount: saleCalculator.roundNumericValue(totalSaleValue + totalTaxCharged + totalCharges - totalDiscount - totalRefunds),
        PaymentMode: getFBRPaymentMode(sale),
        InvoiceType: getFBRInvoiceType(sale),
        Items: invoiceItems
    }

    return invoice;
}

const fbrInvoiceTypes = {
    new: 1,
    debit: 2,
    credit: 3
}

const fbrPaymentMethods = {
    cash: 1,
    card: 2,
    giftVoucher: 3,
    loyaltyCard: 4,
    mixed: 5,
    cheque: 6,
}

function getFBRInvoiceType(sale)
{
    return fbrInvoiceTypes.new;
}

function getFBRPaymentMode(sale)
{
    switch (sale.paymentMethod)
    {
        case constants.PaymentMethodCash:
            return fbrPaymentMethods.cash;
        case constants.PaymentMethodCredit:
            return fbrPaymentMethods.card;
        case constants.PaymentMethodMix:
            return fbrPaymentMethods.mixed;

        case constants.PaymentMethodVenuePaid:
        case constants.PaymentMethodNA:
        default:
            return 0;
    }
}

function getHttpHeaders()
{
    return ({
        "Content-Type": "application/json",
    });
}
